import "@mdotm/utils/debug/install-dbg";
import "./install-preload-links";
import App from "./App";

import ScrollToTop from "$root/components/ScrollToTop";
import { BrowserRouter as Router } from "react-router-dom";

import { initI18n } from "$root/localization/i18n";
import initIubenda, { iubendaPurposeId } from "$root/third-party-integrations/initIubenda";
import initMixPanel from "$root/third-party-integrations/initMixPanel";
import initHotjar from "$root/third-party-integrations/initHotjar";
import { QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@mdotm/shadcn/ui";
import env from "$root/env";
import { initMDOTUI } from "@mdotm/shadcn/legacy/components";
import { createRoot } from "react-dom/client";
import React from "react";

if (env.appEnv === "development" && env.traceHooks) {
	const crazyThreshold = 1000;

	const knownIssues = new Set<string>();

	// eslint-disable-next-line no-inner-declarations
	function makeTracer() {
		for (const [hook, hookName] of [
			[React.useEffect as any, "useEffect"],
			[React.useMemo as any, "useMemo"],
			[React.useCallback as any, "useCallback"],
			[React.useLayoutEffect as any, "useLayoutEffect"],
		] as const) {
			let susHookCalls = 0;
			setInterval(() => {
				susHookCalls = 0;
			}, 1000);

			React[hookName] = (...params: any) => {
				susHookCalls++;
				const stackTrace = String(new Error().stack?.split("\n").slice(0, 3).join("\n"));
				if (susHookCalls > crazyThreshold && !knownIssues.has(stackTrace)) {
					console.trace("here is the trace");
					platformToast({
						icon: "summary",
						children: `Mmm... you had ${susHookCalls} ${hookName}s per second, check the console for the current stack trace`,
						severity: "error",
					});
					susHookCalls = 0;
					knownIssues.add(stackTrace);
				}
				return hook(...params);
			};
		}
	}
	makeTracer();
}

import "$root/styles/index.css";
import { queryClient } from "./third-party-integrations/react-query/typedQueryClient";
import { initAuth } from "./functional-areas/user/initAuth";
import { platformToast } from "./notification-system/toast";
import { initServerEvents } from "./event-bus";

/* (async () => { */ (() => {
	initMDOTUI();
	initAuth();
	initServerEvents();

	initI18n().catch(console.warn);

	// TODO: remove once we have a proper consent solution.
	const bypassIubenda = true;

	if (env.appEnv === "production") {
		console.log = () => undefined;
		console.error = () => undefined;
		console.warn = () => undefined;
	}

	const renderRoot = createRoot(document.getElementById("root")!);
	renderRoot.render(
		<QueryClientProvider client={queryClient}>
			{/* App-wide Radix tooltip context (default 700ms hover delay). Lets any
			    component use the @mdotm/shadcn/ui Tooltip primitives without mounting
			    its own provider. */}
			<TooltipProvider>
				<Router
					getUserConfirmation={() => {
						/* Empty callback to block the default browser prompt */
					}}
				>
					<ScrollToTop />
					<App />
				</Router>
			</TooltipProvider>
		</QueryClientProvider>,
	);

	let previousPreference: boolean | null = null;
	initIubenda("en", (preferences) => {
		if (previousPreference === null) {
			previousPreference = preferences.purposes[4] ?? false;
			return;
		}
		// Page reload to stop hotjar/mixpanel once they have been initialized
		if (!preferences.purposes[iubendaPurposeId] && previousPreference) {
			window.location.reload();
		}

		previousPreference = preferences.purposes[iubendaPurposeId] ?? false;
	}); // Iubenda Script
	initHotjar(env.hotjarKey, 6, String(iubendaPurposeId), bypassIubenda); // Hotjar test ( with Iubenda activate params Script - level 4)

	if (env.appEnv === "production") {
		// only enabled in production and, depending on the user, in staging (look up initMixPanel call sites to find this criterion)
		initMixPanel(env.mixPanelKey, true, String(iubendaPurposeId), bypassIubenda); // MixPanel Script( with Iubenda activate params Script - level 4)
	}

	// If you want to start measuring performance in your app, pass a function
	// to log results (for example: reportWebVitals(console.log))
	// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
	//reportWebVitals();
})(); /* .catch(console.warn); */
